<template>
  <div>
    <div v-if="isOptionMode" class="mb-2">
      <h4>{{ $t('esgPlatform.dimitraModules') }}</h4>
    </div>

    <v-row class="my-4">
      <v-col cols="4">
        <div class="mb-2">
          <span class="h3 mr-1">{{ $t('esgPlatform.selectDimitraApp') }}</span>
        </div>
        <v-select
          :items="dimitraApps"
          v-model="selectedDimitraApp"
          dense
          outlined
          :clearable="!forcedDimitraApp"
          :disabled="!!forcedDimitraApp"
          @change="handleDimitraAppChange"
        ></v-select>
      </v-col>

      <v-col cols="4">
        <div class="mb-2">
          <span class="h3 mr-1">{{ $t('esgPlatform.selectModule') }}</span>
        </div>
        <v-select :items="availableDimitraModules" v-model="localModule.modules" item-text="text" item-value="value" dense outlined clearable multiple
          small-chips :disabled="!selectedDimitraApp" @change="handleDimitraModulesChange"></v-select>
      </v-col>

    </v-row>

    <v-card elevation="0" class="gray lighten-4 my-4 px-4 py-4">
      <v-row align="center" class="mb-2">

        <v-col cols="6">
          <h3>{{ $t('esgPlatform.dimitraModuleDataFields') }}</h3>
        </v-col>

        <v-col cols="6" class="d-flex justify-end align-center">
          <span class="mr-2">{{ $t('esgPlatform.selection') }}</span>

          <v-select class="dimitra-selection" :items="dimitraSelectionOptions" v-model="localModule.selectionAction"
            dense outlined hide-details @change="applyDimitraSelection"></v-select>

          <v-btn icon @click="toggleDimitraDataFields">
            <v-icon>{{ localModule.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-btn>

        </v-col>
      </v-row>

      <div v-if="localModule.expanded">
        <v-tabs v-if="dimitraSelectedModules.length > 1" v-model="localModule.activeModuleIndex" class="mb-3"
          @change="handleDimitraModuleTabChange">

          <v-tab v-for="module in dimitraSelectedModules" :key="module">
            {{ getModuleDisplayLabel(module) }}
          </v-tab>

        </v-tabs>

        <div v-if="dimitraSelectedModules.length === 0" class="text--disabled">
          {{ $t('esgPlatform.selectModuleToViewDataFields') }}
        </div>

        <template v-else>
          <v-card elevation="0" class="options dark mt-2 mb-4">
            <v-card-title>
              <v-row>
                <v-col class="py-2" cols="5">{{ $t('esgPlatform.dataField') }}</v-col>
                <v-col class="py-2" cols="3">{{ $t('esgPlatform.type') }}</v-col>
                <v-col class="py-2" cols="4">{{ $t('esgPlatform.retrieveData') }}</v-col>
              </v-row>
            </v-card-title>
          </v-card>

          <div v-if="activeDimitraFields.length === 0" class="text--disabled">
            {{ $t('esgPlatform.noDataFieldsAvailable') }}
          </div>

          <!-- ✅ FIX: Label resolved from catalog via getCatalogLabel, never from field.label -->
          <template v-for="(field, i) in activeDimitraFields">
            <v-card v-if="isGroupField(field)" :key="`${activeDimitraModule}-${field.key || i}`" elevation="0" outlined
              class="my-2 group-wrapper">
              <div class="group-header px-4 py-3">
                <v-row>
                  <v-col class="py-2 content-col" cols="5">{{ getCatalogLabel(activeDimitraModule, field.key) }}</v-col>
                  <v-col class="py-2 content-col" cols="3">{{ getCatalogDisplayType(activeDimitraModule, field.key, field.type) }}</v-col>
                  <v-col class="py-2 content-col" cols="4">
                    <v-row>
                      <v-col cols="4">
                        {{ field.isEnabled ? $t('esgPlatform.yes') : $t('esgPlatform.no') }}
                      </v-col>
                      <v-col cols="8">
                        <v-switch inset class="bottomless pt-0 mt-0" v-model="field.isEnabled"
                          :disabled="isForcedEnabledField(activeDimitraModule, field.key)"
                          @change="handleGroupToggle(activeDimitraModule, field)"></v-switch>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>

              <div v-if="getGroupChildren(activeDimitraModule, field.key).length" class="group-body px-4 pb-3">
                <v-card v-for="(child, childIndex) in getGroupChildren(activeDimitraModule, field.key)"
                  :key="`${activeDimitraModule}-${field.key}-${child.key || childIndex}`" elevation="0" outlined
                  class="my-2 px-4 py-2 group-child-field">
                  <v-row>
                    <v-col class="py-0 content-col" cols="5">{{ getCatalogLabel(activeDimitraModule, child.key) }}</v-col>
                    <v-col class="py-0 content-col" cols="3">{{ getCatalogDisplayType(activeDimitraModule, child.key, child.type) }}</v-col>
                    <v-col class="py-0 content-col" cols="4">
                      <v-row>
                        <v-col cols="4" class="px-3 py-0">
                          {{ child.isEnabled ? $t('esgPlatform.yes') : $t('esgPlatform.no') }}
                        </v-col>
                        <v-col cols="8" class="px-5 py-0">
                          <v-switch inset class="bottomless pt-0 mt-0" v-model="child.isEnabled"
                            :disabled="!field.isEnabled || isForcedEnabledField(activeDimitraModule, child.key)"
                            @change="handleGroupChildToggle(activeDimitraModule, field)"></v-switch>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card>
              </div>
            </v-card>

            <v-card v-else :key="`${activeDimitraModule}-${field.key || i}`" elevation="0" outlined
              class="my-2 px-4 py-3">
              <v-row>
                <v-col class="py-2 content-col" cols="5">{{ getCatalogLabel(activeDimitraModule, field.key) }}</v-col>
                <v-col class="py-2 content-col" cols="3">{{ getCatalogDisplayType(activeDimitraModule, field.key, field.type) }}</v-col>
                <v-col class="py-2 content-col" cols="4">
                  <v-row>
                    <v-col cols="4">
                      {{ field.isEnabled ? $t('esgPlatform.yes') : $t('esgPlatform.no') }}
                    </v-col>
                    <v-col cols="8">
                      <v-switch inset class="bottomless pt-0 mt-0" v-model="field.isEnabled"
                        :disabled="isForcedEnabledField(activeDimitraModule, field.key)"></v-switch>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </template>
        </template>
      </div>
    </v-card>

    <template v-if="showProgressMetrics">
      <!-- <h3 class="mb-3">{{ $t('esgPlatform.progressMetricRules') }}</h3> -->

      <v-card v-for="(rule, ruleIndex) in localModule.progressMetrics.rules" :key="`rule-${ruleIndex}`" elevation="0"
        class="gray lighten-4 mb-5 px-4 py-4 progress-metric-card">
        <v-row>
          <v-col cols="3">
            <label>{{ $t('esgPlatform.type') }}</label>
            <v-select class="bg-white" :items="progressMetricTypes" item-text="label" item-value="value" v-model="rule.type" outlined
              @change="handleMetricTypeChange(rule)"></v-select>
          </v-col>
          <v-col cols="3" v-if="rule.type !== 'Formula'">
            <label>{{ $t('esgPlatform.dataField') }}</label>
            <v-select :items="getMetricFieldOptions(rule)" v-model="rule.dataField" outlined clearable
              @change="syncProgressMetricRule(rule)"></v-select>
          </v-col>
          <v-col cols="3" v-else>
            <label>{{ $t('esgPlatform.metricTitle') }}</label>
            <v-text-field v-model="rule.title" dense outlined :placeholder="$t('esgPlatform.enterTitle')"
              @input="syncProgressMetricRule(rule)"></v-text-field>
          </v-col>
          <v-col cols="3" v-if="isMeasurementRule(rule)">
            <label>{{ $t('esgPlatform.unit') }}</label>
            <v-select :items="getUnitOptionsForRule(rule)" v-model="rule.unitId" outlined clearable
              :loading="isUnitOptionsLoadingForRule(rule)"
              @focus="onRuleUnitDropdownFocus(rule)" @change="handleRuleUnitChange(rule)">
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-title v-if="isUnitOptionsLoadingForRule(rule)" class="text-center">
                    <v-progress-circular indeterminate size="20" color="primary"></v-progress-circular>
                  </v-list-item-title>
                  <v-list-item-title v-else>{{ $t('esgPlatform.noData') }}</v-list-item-title>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
          <v-col cols="3" v-else></v-col>
          <v-col cols="3" class="d-flex justify-end align-start">
            <v-icon color="red" class="pointer" @click="removeProgressMetricRule(ruleIndex)">
              mdi-close
            </v-icon>
          </v-col>
        </v-row>

        <div v-if="rule.type === 'Formula'" class="formula-builder">
          <v-row class="align-center">
            <template v-for="(part, partIndex) in rule.formula.parts">
              <v-col cols="12" md="4" :key="`part-${ruleIndex}-${partIndex}`">
                <div class="formula-part">

                  <v-icon v-if="rule.formula.parts.length > 1" class="formula-remove pointer" color="red"
                    @click="removeFormulaPart(rule, partIndex)">
                    mdi-close
                  </v-icon>

                  <label>{{ $t('esgPlatform.module') }}</label>
                  <v-select :items="dimitraSelectedModuleOptions" v-model="part.module" item-text="text" item-value="value" dense outlined clearable
                    @change="handleFormulaModuleChange(rule, partIndex)"></v-select>

                  <label>{{ $t('esgPlatform.datafield') }}</label>
                  <v-select :items="getFormulaFieldOptions(part.module)" v-model="part.fieldKey" dense outlined
                    clearable @change="handleFormulaPartFieldChange(part)"></v-select>

                  <template v-if="isMeasurementFormulaPart(part)">
                    <label>{{ $t('esgPlatform.unit') }}</label>
                    <v-select :items="getUnitOptionsForFormulaPart(part)" v-model="part.unitId" dense outlined
                      clearable :loading="isUnitOptionsLoadingForFormulaPart(part)"
                      @focus="onFormulaUnitDropdownFocus(part)"
                      @change="handleFormulaPartUnitChange(part)">
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-title v-if="isUnitOptionsLoadingForFormulaPart(part)" class="text-center">
                            <v-progress-circular indeterminate size="20" color="primary"></v-progress-circular>
                          </v-list-item-title>
                          <v-list-item-title v-else>{{ $t('esgPlatform.noData') }}</v-list-item-title>
                        </v-list-item>
                      </template>
                    </v-select>
                  </template>
                </div>
              </v-col>
              <v-col cols="12" md="2" :key="`op-${ruleIndex}-${partIndex}`" class="d-flex align-center justify-center"
                v-if="partIndex < rule.formula.parts.length - 1">
                <v-select class="formula-operator" :items="formulaOperators" item-text="label" item-value="value"
                  v-model="rule.formula.operators[partIndex]" dense outlined></v-select>
              </v-col>
            </template>
          </v-row>
          <div class="d-flex align-center flex-wrap">
            <v-btn text class="add-link" @click="addFormulaPart(rule)">
              <v-icon left color="primary">mdi-plus-circle-outline</v-icon>
              {{ $t('esgPlatform.addAnotherModule') }}
            </v-btn>
            <span v-if="buildFormulaExpression(rule)" class="ml-2 text--secondary text--small">
              {{ $t('esgPlatform.formulaPreview') }} {{ buildFormulaExpression(rule) }}
            </span>
          </div>
        </div>

        <div class="conditions">
          <h4 class="mb-2">{{ $t('esgPlatform.conditions') }}</h4>
          <v-card v-for="(condition, conditionIndex) in rule.conditions"
            :key="`condition-${ruleIndex}-${conditionIndex}`" elevation="0" class="condition-row mb-3 px-3 py-3">
            <v-row align="center">
              <v-col cols="1">{{ $t('esgPlatform.if') }}</v-col>

              <v-col cols="3">
                <v-text-field :value="getConditionLabel(rule)" dense outlined readonly
                  class="readonly-field"></v-text-field>
              </v-col>

              <v-col cols="2">
                <v-select :items="getConditionOperators(rule)" v-model="condition.operator" dense outlined></v-select>
              </v-col>
              
              <v-col cols="3">
                <v-autocomplete 
                  v-if="isCropTypeField(rule)"
                  v-model="condition.value"
                  :items="getCropOptions(`${ruleIndex}-${conditionIndex}`)"
                  :loading="isCropLoading(`${ruleIndex}-${conditionIndex}`)"
                  dense outlined clearable
                  :placeholder="$t('esgPlatform.select')"
                  item-text="text"
                  item-value="value"
                  class="white"
                  :search-input.sync="cropSearchMap[`${ruleIndex}-${conditionIndex}`]"
                  @update:search-input="(val) => onCropSearch(val, `${ruleIndex}-${conditionIndex}`)"
                  @focus="onCropDropdownFocus(`${ruleIndex}-${conditionIndex}`)"
                >
                  <template v-slot:append-item>
                    <div v-if="isCropLoading(`${ruleIndex}-${conditionIndex}`)" class="pa-4 text-center">
                      <v-progress-circular indeterminate size="20" color="primary"></v-progress-circular>
                    </div>
                  </template>
                </v-autocomplete>
                <v-autocomplete v-else-if="isCropVarietyField(rule)"
                  v-model="condition.value"
                  :items="getCropVarietyOptions(`${ruleIndex}-${conditionIndex}`)"
                  :loading="isCropVarietyLoading(`${ruleIndex}-${conditionIndex}`)"
                  :search-input.sync="cropVarietySearchMap[`${ruleIndex}-${conditionIndex}`]"
                  dense outlined clearable
                  :placeholder="$t('esgPlatform.select')"
                  item-text="text"
                  item-value="value"
                  class="white"
                  @update:search-input="(val) => onCropVarietySearch(val, `${ruleIndex}-${conditionIndex}`)"
                  @focus="onCropVarietyDropdownFocus(`${ruleIndex}-${conditionIndex}`)"
                >
                  <template v-slot:append-item>
                    <div v-if="isCropVarietyLoading(`${ruleIndex}-${conditionIndex}`)" class="pa-4 text-center">
                      <v-progress-circular indeterminate size="20" color="primary"></v-progress-circular>
                    </div>
                  </template>
                </v-autocomplete>
                <v-select v-else-if="getConditionValueInputType(rule) === 'select'" :items="getConditionValueOptions(rule)"
                  v-model="condition.value" dense outlined clearable></v-select>
                <v-text-field v-else v-model="condition.value" :type="getConditionValueInputType(rule)"
                  :suffix="getConditionValueSuffix(rule)" dense outlined></v-text-field>
              </v-col>

              <v-col cols="3" class="d-flex align-center justify-end">
                <span class="mr-2">{{ $t('esgPlatform.score') }}</span>
                <v-text-field v-model="condition.score" type="number" dense outlined class="score-input"></v-text-field>
                <v-icon class="ml-2 pointer" @click="removeMetricCondition(rule, conditionIndex)">
                  mdi-close
                </v-icon>
              </v-col>
            </v-row>
          </v-card>
          <v-btn text class="add-link" @click="addMetricCondition(rule)">
            <v-icon left color="primary">mdi-plus-circle-outline</v-icon>
            {{ $t('esgPlatform.addNewCondition') }}
          </v-btn>
        </div>
      </v-card>

      <v-btn color="primary" class="mb-6" @click="addProgressMetricRule">
        {{ $t('esgPlatform.addProgressMetric') }}
      </v-btn>

      <!-- <v-row class="mb-2">
        <v-col cols="6">
          <div class="mb-2">
            <span class="h3 mr-1">{{ $t('esgPlatform.yearOnYearComparisonScore') }}</span>
          </div>
        </v-col>
        <v-col cols="2">
          <v-row>
            <v-col>{{ localModule.progressMetrics.yearOnYear.enabled ? $t('esgPlatform.enabled') : $t('esgPlatform.disabled')}}</v-col>
            <v-col>
              <v-switch inset class="bottomless pt-0 mt-0"
                v-model="localModule.progressMetrics.yearOnYear.enabled"></v-switch>
            </v-col>
          </v-row>
        </v-col>
      </v-row> -->

      <div v-if="localModule.progressMetrics.yearOnYear.enabled">
        <v-card elevation="0" class="gray lighten-4 px-4 py-4 mb-4">
          <v-col cols="6" class="px-0">
            <label>{{ $t('esgPlatform.selectProgressMetric') }}</label>
            <v-select :items="progressMetricOptions" v-model="localModule.progressMetrics.yearOnYear.metricKey" dense
              outlined clearable @change="handleYoYMetricChange"></v-select>
          </v-col>

          <v-card v-for="(condition, conditionIndex) in localModule.progressMetrics.yearOnYear.conditions"
            :key="`yearOnYear-condition-${conditionIndex}`" elevation="0" class="condition-row mb-3 px-3 py-3">
            <v-row align="center">
              <v-col cols="1">{{ $t('esgPlatform.if') }}</v-col>
              <v-col cols="3">
                <v-text-field :value="getYoYMetricLabel()" dense outlined readonly
                  class="readonly-field"></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-select :items="yoYComparisonOptions" v-model="condition.direction" dense outlined></v-select>
              </v-col>
              <v-col cols="2">
                <v-text-field v-model="condition.percent" type="number" dense outlined suffix="%"></v-text-field>
              </v-col>
              <v-col cols="3" class="d-flex align-center justify-end">
                <span class="mr-2">{{ $t('esgPlatform.score') }}</span>
                <v-text-field v-model="condition.score" type="number" dense outlined class="score-input"></v-text-field>
                <v-icon class="ml-2 pointer" @click="removeYoYCondition(conditionIndex)">
                  mdi-close
                </v-icon>
              </v-col>
            </v-row>
          </v-card>

          <v-btn text class="add-link" @click="addYoYCondition">
            <v-icon left color="primary">mdi-plus-circle-outline</v-icon>
            {{ $t('esgPlatform.addNewCondition') }}
          </v-btn>
        </v-card>
      </div>
    </template>
  </div>
</template>

<script>

import {
  DIMITRA_CONFIG_TEMPLATE,
  DIMITRA_LEGACY_DATAFIELD_MAP,
  getDimitraAppForRespondentType,
  normalizeDimitraAppValue as normalizeDimitraAppConfigValue,
} from "../constants/esgDimitraModule";
import EsgService from "../../../../_services/EsgService";
import _ from "lodash";

export default {
  name: "DimitraModule",
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: "question",
    },
    scoreEnabled: {
      type: Boolean,
      default: false,
    },
    surveyRespondentType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      localModule: this.getDefaultDimitraModuleState(),
      syncingFromParent: false,
      lastCalculatedScore: null,
      cropSearchMap: {},
      cropStateMap: {},
      activeCropKey: null,
      cropLimit: 50,
      cropVarietySearchMap: {},
      cropVarietyStateMap: {},
      activeCropVarietyKey: null,
      cropVarietyLimit: 50,
      unitTypeCache: {},
      ...DIMITRA_CONFIG_TEMPLATE
    };
  },
  computed: {
    isOptionMode() {
      return this.mode === "option";
    },
    isQuestionMode() {
      return this.mode === "question";
    },
    showProgressMetrics() {
      return this.isQuestionMode && this.scoreEnabled;
    },
    selectedDimitraApp: {
      get() {
        return this.forcedDimitraApp || this.normalizeDimitraAppValue(this.localModule.app);
      },
      set(app) {
        this.setIfChanged(this.localModule, "app", this.normalizeDimitraAppValue(app));
      },
    },
    availableDimitraModules() {
      const app = this.selectedDimitraApp;
      if (app && this.dimitraModulesByApp[app]) {
        return this.dimitraModulesByApp[app].map((module) => ({
          text: this.getModuleDisplayLabel(module),
          value: module,
        }));
      }
      return [];
    },
    forcedDimitraApp() {
      return getDimitraAppForRespondentType(this.surveyRespondentType);
    },
    dimitraSelectedModules() {
      return this.localModule.modules || [];
    },
    dimitraSelectedModuleOptions() {
      return this.dimitraSelectedModules.map((module) => ({
        text: this.getModuleDisplayLabel(module),
        value: module,
      }));
    },
    activeDimitraModule() {
      const index = Number.isInteger(this.localModule.activeModuleIndex) ? this.localModule.activeModuleIndex : 0;
      return this.dimitraSelectedModules[index] || this.dimitraSelectedModules[0] || null;
    },
    activeDimitraFieldsAll() {
      const moduleName = this.activeDimitraModule;
      return moduleName ? (this.localModule.fieldsByModule[moduleName] || []) : [];
    },
    activeDimitraFields() {
      return this.activeDimitraFieldsAll.filter((field) => !field?.isChild);
    },
    progressMetricOptions() {
      return (this.localModule.progressMetrics?.rules || []).map((rule) => ({
        value: this.getProgressMetricRuleKey(rule),
        text: this.getProgressMetricLabel(rule),
      }));
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        const normalized = this.normalizeDimitraModuleState(newValue || {});
        if (this.forcedDimitraApp) {
          normalized.app = this.forcedDimitraApp;
        }
        if (this.serializeForCompare(normalized) === this.serializeForCompare(this.localModule)) {
          return;
        }
        this.syncingFromParent = true;
        this.localModule = normalized;
        this.applySurveyRespondentDimitraApp();
        this.syncProgressMetricScores();
        this.$emit("input", this.cloneObject(this.localModule));
        this.$nextTick(() => {
          this.syncingFromParent = false;
        });
      },
    },
    scoreEnabled: {
      immediate: true,
      handler() {
        this.syncProgressMetricScores();
      },
    },
    surveyRespondentType: {
      immediate: true,
      handler() {
        this.applySurveyRespondentDimitraApp();
      },
    },
    "localModule.progressMetrics": {
      deep: true,
      handler() {
        if (this.syncingFromParent) {
          return;
        }
        this.syncProgressMetricScores();
      },
    },
    localModule: {
      deep: true,
      handler(newValue) {
        if (this.syncingFromParent) {
          return;
        }
        this.$emit("input", this.cloneObject(newValue));
      },
    },
    "localModule.modules": {
      handler(newVal) {
        this.syncDimitraModules(newVal);
      },
      deep: true,
    },
    "localModule.fieldsByModule": {
      handler() {
        this.updateSelectionActionForActiveModule();
      },
      deep: true,
    },
    "localModule.activeModuleIndex"() {
      this.updateSelectionActionForActiveModule();
    },
  },
  methods: {
    getDefaultCropState() {
      return {
        options: [],
        loading: false,
        search: "",
        page: 1,
        isFetching: false,
        lastFetchedCount: 0,
        requestId: 0,
      };
    },
    getCropState(key) {
      return this.cropStateMap[key] || this.getDefaultCropState();
    },
    setCropState(key, patch = {}) {
      const nextState = {
        ...this.getCropState(key),
        ...patch,
      };
      this.$set(this.cropStateMap, key, nextState);
      return nextState;
    },
    getCropOptions(key) {
      return this.getCropState(key).options;
    },
    isCropLoading(key) {
      return this.getCropState(key).loading;
    },
    hasMoreCrops(key) {
      const state = this.getCropState(key);
      if (state.options.length === 0) return true;
      return state.lastFetchedCount >= this.cropLimit;
    },
    isSelectedCropSearchValue(value, key) {
      return this.getCropOptions(key).some((option) => option.value === value || option.text === value);
    },
    normalizeCropSearch(value, key) {
      if (value === "" || value === null || value === undefined) {
        return "";
      }
      if (this.isSelectedCropSearchValue(value, key)) {
        return null;
      }
      return value;
    },
    onCropSearch: _.debounce(function (val, key) {
      this.$set(this.cropSearchMap, key, val);

      const normalizedSearch = this.normalizeCropSearch(val, key);
      if (normalizedSearch === null) {
        this.setCropState(key, { search: "" });
        return;
      }

      this.resetCropState(key, { search: normalizedSearch });
      this.fetchCrops({ key, page: 1, search: normalizedSearch || null, append: false });
    }, 500),
    resetCropState(key, patch = {}) {
      this.setCropState(key, {
        options: [],
        loading: false,
        search: "",
        page: 1,
        isFetching: false,
        lastFetchedCount: 0,
        requestId: this.getCropState(key).requestId,
        ...patch,
      });
    },
    getDefaultCropVarietyState() {
      return {
        options: [],
        loading: false,
        search: "",
        page: 1,
        isFetching: false,
        lastFetchedCount: 0,
        requestId: 0,
      };
    },
    getCropVarietyState(key) {
      return this.cropVarietyStateMap[key] || this.getDefaultCropVarietyState();
    },
    setCropVarietyState(key, patch = {}) {
      const nextState = {
        ...this.getCropVarietyState(key),
        ...patch,
      };
      this.$set(this.cropVarietyStateMap, key, nextState);
      return nextState;
    },
    getCropVarietyOptions(key) {
      return this.getCropVarietyState(key).options;
    },
    isCropVarietyLoading(key) {
      return this.getCropVarietyState(key).loading;
    },
    hasMoreCropVarieties(key) {
      const state = this.getCropVarietyState(key);
      if (state.options.length === 0) return true;
      return state.lastFetchedCount >= this.cropVarietyLimit;
    },
    isSelectedCropVarietySearchValue(value, key) {
      return this.getCropVarietyOptions(key).some((option) => option.value === value || option.text === value);
    },
    normalizeCropVarietySearch(value, key) {
      if (value === "" || value === null || value === undefined) {
        return "";
      }
      if (this.isSelectedCropVarietySearchValue(value, key)) {
        return null;
      }
      return value;
    },
    onCropVarietySearch: _.debounce(function (val, key) {
      this.$set(this.cropVarietySearchMap, key, val);

      const normalizedSearch = this.normalizeCropVarietySearch(val, key);
      if (normalizedSearch === null) {
        this.setCropVarietyState(key, { search: "" });
        return;
      }

      this.resetCropVarietyState(key, { search: normalizedSearch });
      this.fetchCropVarieties({ key, page: 1, search: normalizedSearch || null, append: false });
    }, 500),
    resetCropVarietyState(key, patch = {}) {
      this.setCropVarietyState(key, {
        options: [],
        loading: false,
        search: "",
        page: 1,
        isFetching: false,
        lastFetchedCount: 0,
        requestId: this.getCropVarietyState(key).requestId,
        ...patch,
      });
    },
    shouldRefetchAutocompleteOptions(state, activeSearch) {
      if (!state) {
        return true;
      }
      return state.options.length === 0 || state.search !== activeSearch;
    },
    // ✅ PRIMARY LABEL RESOLVER — always reads from catalog, never from field object
    // All UI rendering of field names must go through this method exclusively.
    findCatalogFieldByKey(moduleName, fieldKey) {
      if (!moduleName || !fieldKey) {
        return null;
      }
      const search = (fields = []) => {
        for (const field of fields) {
          if (!field) continue;
          if (field.key === fieldKey) {
            return field;
          }
          if (field.type === "Group" && Array.isArray(field.children) && field.children.length) {
            const match = search(field.children);
            if (match) {
              return match;
            }
          }
        }
        return null;
      };

      const moduleFields = DIMITRA_CONFIG_TEMPLATE.dimitraModuleCatalog[moduleName] || [];
      return search(moduleFields);
    },
    getCatalogFieldsFlat(moduleName) {
      const moduleFields = DIMITRA_CONFIG_TEMPLATE.dimitraModuleCatalog[moduleName] || [];
      const flat = [];

      const walk = (fields = []) => {
        for (const field of fields) {
          if (!field) continue;
          flat.push(field);
          if (field.type === "Group" && Array.isArray(field.children) && field.children.length) {
            walk(field.children);
          }
        }
      };

      walk(moduleFields);
      return flat;
    },
    getCatalogLabel(moduleName, key) {
      if (!moduleName || !key) return key || "";
      const i18nKey = `esgPlatform.dimitraModuleCatalog.fields.${key}`;
      const translated = this.$t(i18nKey);
      if (translated !== i18nKey) return translated;
      const match = this.findCatalogFieldByKey(moduleName, key);
      return match?.label || key;
    },
    getCatalogDisplayType(moduleName, key, fallbackType = "") {
      if (!moduleName || !key) {
        return fallbackType || "";
      }
      const meta = this.findCatalogFieldByKey(moduleName, key);
      if (meta?.valueType === "ID") {
        return "ID";
      }
      return fallbackType || meta?.type || "";
    },
    isChildField(field) {
      return !!field?.isChild;
    },
    getGroupChildren(moduleName, groupKey) {
      if (!moduleName || !groupKey) {
        return [];
      }
      const fields = this.localModule.fieldsByModule?.[moduleName] || [];
      return fields.filter((field) => this.isChildField(field) && field.groupKey === groupKey);
    },
    isGroupField(field) {
      return !!field && (field.type === "Group" || (Array.isArray(field.children) && field.children.length));
    },
    isCropTypeField(rule) {
      if (!rule || !rule.dataField) return false;
      const { fieldKey } = this.parseFieldValue(rule.dataField);
      return ['irrigation_cropType', 'harvest_cropType', 'cropType'].includes(fieldKey);
    },
    isCropVarietyField(rule) {
      if (!rule || !rule.dataField) return false;
      const { fieldKey } = this.parseFieldValue(rule.dataField);
      return ['irrigation_cropVariety', 'harvesting_variety'].includes(fieldKey);
    },
    syncGroupFieldEnabled(moduleName, groupField) {
      if (!this.isGroupField(groupField)) {
        return;
      }
      const resolvedModule = moduleName || this.activeDimitraModule;
      const children = this.getGroupChildren(resolvedModule, groupField.key);
      if (!children.length) {
        return;
      }
      groupField.isEnabled = children.some((child) => !!child?.isEnabled);
    },
    handleGroupToggle(moduleName, groupField) {
      if (!this.isGroupField(groupField)) {
        return;
      }
      const resolvedModule = moduleName || this.activeDimitraModule;
      const nextEnabled = !!groupField.isEnabled;
      this.getGroupChildren(resolvedModule, groupField.key).forEach((child) => {
        child.isEnabled = this.isForcedEnabledField(resolvedModule, child.key) ? true : nextEnabled;
      });
      this.syncGroupFieldEnabled(resolvedModule, groupField);
    },
    handleGroupChildToggle(moduleName, groupField) {
      if (!this.isGroupField(groupField)) {
        return;
      }
      const resolvedModule = moduleName || this.activeDimitraModule;
      this.syncGroupFieldEnabled(resolvedModule, groupField);
    },
    flattenDimitraFieldStates(fields = []) {
      const flat = [];
      const walk = (field) => {
        if (!field) return;
        flat.push(field);
        if (this.isGroupField(field) && Array.isArray(field.children) && field.children.length) {
          field.children.forEach(walk);
        }
      };
      (fields || []).forEach(walk);
      return flat;
    },

    cloneObject(value) {
      return JSON.parse(JSON.stringify(value || {}));
    },
    serializeForCompare(value) {
      try {
        return JSON.stringify(value || {});
      } catch (error) {
        return "error";
      }
    },
    parseScore(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    setIfChanged(target, key, nextValue) {
      if (!target || target[key] === nextValue) {
        return false;
      }
      this.$set(target, key, nextValue);
      return true;
    },
    getMaxConditionScore(conditions = []) {
      if (!Array.isArray(conditions) || !conditions.length) {
        return 0;
      }
      return conditions.reduce((maxScore, condition) => {
        const normalized = this.parseScore(condition?.score);
        return normalized > maxScore ? normalized : maxScore;
      }, 0);
    },
    getProgressMetricRulesMaxScore() {
      const rules = this.localModule?.progressMetrics?.rules || [];
      return rules.reduce((total, rule) => total + this.getMaxConditionScore(rule.conditions), 0);
    },
    getYearOnYearMaxScore() {
      const yearOnYear = this.localModule?.progressMetrics?.yearOnYear;
      if (!yearOnYear?.enabled) {
        return 0;
      }
      return this.getMaxConditionScore(yearOnYear.conditions);
    },
    syncProgressMetricScores() {
      if (!this.localModule?.progressMetrics) {
        return;
      }

      if (!this.localModule.progressMetrics.yearOnYear) {
        this.$set(this.localModule.progressMetrics, "yearOnYear", {
          enabled: false,
          metricKey: null,
          conditions: [],
        });
      }

      const rulesMaxScore = this.getProgressMetricRulesMaxScore();
      const yoYMaxScore = this.getYearOnYearMaxScore();
      const totalScore = rulesMaxScore + yoYMaxScore;

      this.setIfChanged(this.localModule, "score", totalScore);

      if (this.lastCalculatedScore !== totalScore) {
        this.lastCalculatedScore = totalScore;
        this.$emit("score-change", totalScore);
      }
    },
    getDefaultProgressMetricsState() {
      return {
        rules: [],
        yearOnYear: {
          enabled: false,
          metricKey: null,
          conditions: [],
        },
      };
    },
    getDefaultDimitraModuleState() {
      return {
        score: 0,
        app: null,
        modules: [],
        fieldsByModule: {},
        expanded: true,
        progressMetrics: this.getDefaultProgressMetricsState(),
      };
    },
    normalizeDimitraAppValue(app) {
      return normalizeDimitraAppConfigValue(app);
    },
    applySurveyRespondentDimitraApp() {
      if (!this.forcedDimitraApp) {
        return;
      }

      this.setIfChanged(this.localModule, "app", this.forcedDimitraApp);

      const allowedModules = this.dimitraModulesByApp[this.forcedDimitraApp] || [];
      const selectedModules = Array.isArray(this.localModule.modules) ? this.localModule.modules : [];
      const validModules = selectedModules.filter((moduleName) => allowedModules.includes(moduleName));

      if (validModules.length !== selectedModules.length) {
        this.$set(this.localModule, "modules", validModules);
        this.syncDimitraModules(validModules);
      }
    },
    toKey(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
    },
    getModuleDisplayLabel(moduleName) {
      if (!moduleName) return "";
      const i18nKey = `esgPlatform.dimitraModuleCatalog.modules.${moduleName}`;
      const translated = this.$t(i18nKey);
      if (translated !== i18nKey) return translated;
      return moduleName === "Soil" ? "Soil Management" : moduleName;
    },
    isForcedEnabledField(moduleName, fieldKey) {
      if (!moduleName || !fieldKey) {
        return false;
      }
      const normalizedModule = this.toKey(moduleName);
      const normalizedKey = this.toKey(fieldKey);
      const fieldMeta = this.findCatalogFieldByKey(moduleName, fieldKey);
      const normalizedLabel = this.toKey(fieldMeta?.label);

      const isFarmIdentifier =
        normalizedKey === "farmid" ||
        normalizedKey === "farm-id" ||
        normalizedLabel === "farm-id" ||
        normalizedLabel === "farm-zone";

      return (
        (normalizedModule === "irrigation" &&
          (normalizedKey === "irrigationfarm" || normalizedKey === "irrigation-farm" || normalizedLabel === "farm-id")) ||
        (
          ["harvesting", "soil", "farms", "landsoil-preparation", "nutrient-management"].includes(normalizedModule) &&
          isFarmIdentifier
        )
      );
    },
    // ✅ Builds fieldsByModule entries using only: key, type, isEnabled (+ group hints)
    // Label is intentionally omitted — it is resolved at render time via getCatalogLabel
    buildDimitraFieldsForModule(moduleName, { defaultEnabled = true } = {}) {
      const fields = DIMITRA_CONFIG_TEMPLATE.dimitraModuleCatalog[moduleName] || [];
      const result = [];

      const pushFieldState = (field, { groupKey = null, isChild = false } = {}) => {
        if (!field?.key) {
          return;
        }

        const isForced = this.isForcedEnabledField(moduleName, field.key);
        const enabledFallback = field.isEnabled !== undefined ? field.isEnabled : defaultEnabled;
        const state = {
          key: field.key,
          type: field.type,
          isEnabled: isForced ? true : enabledFallback,
          value: field.value || null,
        };

        const shouldMarkChild = isChild || !!field.isChild;
        if (shouldMarkChild) {
          state.isChild = true;
          state.groupKey = field.groupKey || groupKey || null;
        }

        result.push(state);
      };

      fields.forEach((field) => {
        if (field?.type === "Group" && Array.isArray(field.children) && field.children.length) {
          pushFieldState(field, { isChild: false });
          field.children.forEach((child) => pushFieldState(child, { isChild: true, groupKey: field.key }));
          return;
        }

        pushFieldState(field);
      });

      // Ensure group toggle reflects children (when present)
      result
        .filter((field) => this.isGroupField(field))
        .forEach((groupField) => {
          const children = result.filter(
            (child) => this.isChildField(child) && child.groupKey === groupField.key
          );
          if (!children.length) {
            return;
          }
          groupField.isEnabled = children.some((child) => !!child?.isEnabled);
        });

      return result;
    },
    normalizeProgressMetrics(data = {}, modules = []) {
      const source = Array.isArray(data) ? { rules: data } : data;
      const rules = Array.isArray(source.rules) ? source.rules : [];
      const defaultModule = modules[0] || null;
      const normalizedRules = rules.map((rule) => {
        const type = rule.type || "State";
        const operator = this.progressMetricOperators[type]?.[0] || "=";
        const normalizedRule = {
          key: this.toKey(rule.key || ""),
          type,
          dataField: this.remapLegacyDataField(rule.dataField || null),
          // Target UnitsList unit this rule's actual value is converted into
          // before comparison. Denormalized (unitTypeId/unitAbbvr) so the
          // suffix/label render correctly even before the unit dropdown's
          // options have been fetched — see handleRuleUnitChange.
          unitId: rule.unitId ?? null,
          unitTypeId: rule.unitTypeId ?? null,
          unitAbbvr: rule.unitAbbvr ?? null,
          title: rule.title || "",
          formula: null,
          conditions: Array.isArray(rule.conditions) && rule.conditions.length
            ? rule.conditions.map((condition) => ({
              key: this.toKey(condition.key || ""),
              operator: condition.operator || operator,
              value: condition.value ?? null,
              score: condition.score ?? null,
            }))
            : [],
          enabled: rule.enabled !== undefined ? rule.enabled : true,
        };

        if (type === "Formula") {
          normalizedRule.formula = rule.formula
            ? {
              parts: Array.isArray(rule.formula.parts) && rule.formula.parts.length
                ? rule.formula.parts.map((part) => ({
                  module: part.module || null,
                  fieldKey: this.remapLegacyFormulaPartFieldKey(part.module, part.fieldKey || null),
                  unitId: part.unitId ?? null,
                  unitTypeId: part.unitTypeId ?? null,
                  unitAbbvr: part.unitAbbvr ?? null,
                }))
                : this.createFormulaDefinition(defaultModule).parts,
              operators: Array.isArray(rule.formula.operators) && rule.formula.operators.length
                ? [...rule.formula.operators]
                : this.createFormulaDefinition(defaultModule).operators,
            }
            : this.createFormulaDefinition(defaultModule);
        }

        if (!normalizedRule.conditions.length) {
          normalizedRule.conditions = [this.createMetricCondition(normalizedRule, operator)];
        }

        this.syncProgressMetricRule(normalizedRule);

        // Warm the unit cache for any unit already chosen on a saved rule, so
        // the dropdown shows the right label without waiting for @focus.
        if (normalizedRule.type === "Formula") {
          (normalizedRule.formula?.parts || []).forEach((part) => {
            if (part.unitId != null) {
              this.ensureUnitsLoaded(this.getUnitTypeForField(part.module, part.fieldKey));
            }
          });
        } else if (normalizedRule.unitId != null) {
          const { moduleName, fieldKey } = this.parseFieldValue(normalizedRule.dataField);
          this.ensureUnitsLoaded(this.getUnitTypeForField(moduleName, fieldKey));
        }

        return normalizedRule;
      });

      return {
        enabled: !!source.enabled,
        rules: normalizedRules,
        yearOnYear: {
          enabled: !!source.yearOnYear?.enabled,
          metricKey: this.toKey(source.yearOnYear?.metricKey || "") || null,
          conditions: Array.isArray(source.yearOnYear?.conditions) && source.yearOnYear.conditions.length
            ? source.yearOnYear.conditions.map((condition) => ({
              key: this.toKey(condition.key || source.yearOnYear?.metricKey || ""),
              direction: condition.direction || this.yoYComparisonOptions[0],
              percent: condition.percent ?? null,
              score: condition.score ?? null,
            }))
            : [],
        },
      };
    },
    // ✅ FIXED: Normalizes saved field data using only key, type, isEnabled.
    // label/title/name from backend are intentionally discarded.
    // The catalog is always the source of truth for labels.
    normalizeDimitraModuleState(settings = {}) {
      const defaultState = this.getDefaultDimitraModuleState();
      const storedFields =
        settings.fieldsByModule && typeof settings.fieldsByModule === "object"
          ? settings.fieldsByModule
          : {};
      const modules = Array.isArray(settings.modules) && settings.modules.length
        ? settings.modules
        : Object.keys(storedFields);
      const fieldsByModule = {};

      modules.forEach((moduleName) => {
        const savedFields = Array.isArray(storedFields[moduleName]) ? storedFields[moduleName] : null;
        if (!savedFields || !savedFields.length) {
          fieldsByModule[moduleName] = this.buildDimitraFieldsForModule(moduleName, { defaultEnabled: true });
          return;
        }

        const savedFieldMap = new Map();
        const savedFieldsInOrder = [];

        const normalizeStoredField = (rawField, { inheritedGroupKey = null } = {}) => {
          if (!rawField || typeof rawField !== "object" || Array.isArray(rawField)) {
            return;
          }
          const fieldKey = rawField.key || rawField.label || rawField.title;
          if (!fieldKey) {
            return;
          }

          const normalized = {
            key: fieldKey,
            type: rawField.type || rawField.dataType || "",
            isEnabled: this.isForcedEnabledField(moduleName, fieldKey)
              ? true
              : (rawField.isEnabled !== undefined
                ? !!rawField.isEnabled
                : (rawField.getData !== undefined ? !!rawField.getData : true)),
            value: rawField.value || null,
            isChild: !!rawField.isChild || !!inheritedGroupKey,
            groupKey: rawField.groupKey || inheritedGroupKey || null,
          };

          if (!savedFieldMap.has(normalized.key)) {
            savedFieldsInOrder.push(normalized);
          }
          savedFieldMap.set(normalized.key, normalized);

          if (Array.isArray(rawField.children) && rawField.children.length) {
            rawField.children.forEach((child) =>
              normalizeStoredField(child, { inheritedGroupKey: fieldKey })
            );
          }
        };

        savedFields.forEach((field) => normalizeStoredField(field));

        const catalogDefaults = this.buildDimitraFieldsForModule(moduleName, { defaultEnabled: false });
        const catalogKeysFlat = new Set(this.getCatalogFieldsFlat(moduleName).map((field) => field.key));

        const merged = catalogDefaults.map((catalogField) => {
          const savedMatch = savedFieldMap.get(catalogField.key);

          if (savedMatch) {
            catalogField.isEnabled = this.isForcedEnabledField(moduleName, catalogField.key) ? true : !!savedMatch.isEnabled;
            if (savedMatch.isChild && !catalogField.isChild) {
              catalogField.isChild = true;
              catalogField.groupKey = savedMatch.groupKey || catalogField.groupKey || null;
            }
            if (catalogField.isChild && !catalogField.groupKey) {
              catalogField.groupKey = savedMatch.groupKey || null;
            }
          }

          return catalogField;
        });

        // Apply group-level toggle (when child states are not explicitly saved)
        merged
          .filter((field) => this.isGroupField(field))
          .forEach((groupField) => {
            const children = merged.filter(
              (child) => this.isChildField(child) && child.groupKey === groupField.key
            );
            if (!children.length) {
              return;
            }

            const savedGroup = savedFieldMap.get(groupField.key);
            const hasAnySavedChild = children.some((child) => savedFieldMap.has(child.key));

            if (savedGroup?.isEnabled === false) {
              children.forEach((child) => {
                child.isEnabled = this.isForcedEnabledField(moduleName, child.key) ? true : false;
              });
            } else if (savedGroup?.isEnabled === true && !hasAnySavedChild) {
              // Nutrient Management's "fertilizerInputs" used to be a flat
              // Text field ("Fertilizer Name") before it became this Group.
              // A config saved under the old shape has isEnabled: true and
              // no saved children, which would otherwise hit this branch
              // and silently enable all 9 new children the first time the
              // question is reopened. Carry forward only the field it
              // actually meant instead.
              const isLegacyNutrientFertilizerInputsField =
                moduleName === "Nutrient Management" &&
                groupField.key === "fertilizerInputs" &&
                savedGroup.type !== "Group";

              children.forEach((child) => {
                const shouldEnable = isLegacyNutrientFertilizerInputsField
                  ? child.key === "fertilizerName"
                  : true;
                child.isEnabled = this.isForcedEnabledField(moduleName, child.key) ? true : shouldEnable;
              });
            }

            groupField.isEnabled = children.some((child) => !!child.isEnabled);
          });

        const extraFields = savedFieldsInOrder
          .filter((field) => !catalogKeysFlat.has(field.key))
          .map((field) => {
            const extra = {
              key: field.key,
              type: field.type,
              isEnabled: field.isEnabled,
            };
            if (field.isChild) {
              extra.isChild = true;
              extra.groupKey = field.groupKey || null;
            }
            return extra;
          });

        fieldsByModule[moduleName] = [...merged, ...extraFields];
      });

      const activeIndex = Number.isInteger(settings.activeModuleIndex)
        ? settings.activeModuleIndex
        : (settings.activeModule && modules.includes(settings.activeModule))
          ? modules.indexOf(settings.activeModule)
          : 0;

      return {
        ...defaultState,
        score: this.parseScore(settings.score),
        app: this.normalizeDimitraAppValue(settings.app),
        modules,
        fieldsByModule,
        activeModuleIndex: activeIndex,
        expanded: settings.expanded !== undefined ? settings.expanded : defaultState.expanded,
        progressMetrics: this.normalizeProgressMetrics(settings.progressMetrics || {}, modules),
      };
    },
    syncDimitraModules(modules = []) {
      const selectedModules = Array.isArray(modules) ? modules : [];
      const fieldsByModule = { ...this.localModule.fieldsByModule };

      selectedModules.forEach((moduleName) => {
        if (!fieldsByModule[moduleName]) {
          fieldsByModule[moduleName] = this.buildDimitraFieldsForModule(moduleName);
        }
      });

      Object.keys(fieldsByModule).forEach((moduleName) => {
        if (!selectedModules.includes(moduleName)) {
          delete fieldsByModule[moduleName];
        }
      });

      this.localModule.fieldsByModule = fieldsByModule;

      const maxIndex = selectedModules.length ? selectedModules.length - 1 : 0;
      const currentIndex = this.localModule.activeModuleIndex ?? 0;
      this.localModule.activeModuleIndex = Math.min(currentIndex, maxIndex);

      this.sanitizeProgressMetrics();
      this.handleDimitraModuleTabChange();
    },
    handleDimitraAppChange() {
      const normalizedApp = this.normalizeDimitraAppValue(this.localModule.app);
      if (normalizedApp !== this.localModule.app) {
        this.localModule.app = normalizedApp;
      }
      if (this.forcedDimitraApp && this.localModule.app !== this.forcedDimitraApp) {
        this.localModule.app = this.forcedDimitraApp;
      }
      this.localModule.modules = [];
      this.localModule.fieldsByModule = {};
      this.localModule.activeModuleIndex = 0;
      this.localModule.selectionAction = "Get All";
      this.sanitizeProgressMetrics();
    },
    handleDimitraModulesChange(modules) {
      this.syncDimitraModules(modules);
    },
    applyDimitraSelection(action) {
      const moduleName = this.activeDimitraModule;
      if (!moduleName) {
        return;
      }
      if (!["Get All", "Clear All"].includes(action)) {
        return;
      }

      const shouldEnable = action === "Get All";
      const fields = this.localModule.fieldsByModule[moduleName] || [];
      fields.forEach((field) => {
        field.isEnabled = this.isForcedEnabledField(moduleName, field.key) ? true : shouldEnable;

        if (this.isGroupField(field)) {
          this.handleGroupToggle(moduleName, field);
        }
      });
      this.updateSelectionActionForActiveModule();
    },
    toggleDimitraDataFields() {
      this.localModule.expanded = !this.localModule.expanded;
    },
    handleDimitraModuleTabChange() {
      this.updateSelectionActionForActiveModule();
    },
    updateSelectionActionForActiveModule() {
      const moduleName = this.activeDimitraModule;
      if (!moduleName) {
        return;
      }
      const fields = this.localModule.fieldsByModule[moduleName] || [];
      const flattenedFields = this.flattenDimitraFieldStates(fields);
      if (!flattenedFields.length) {
        return;
      }
      const enabledCount = flattenedFields.filter((field) => field.isEnabled).length;
      if (enabledCount === 0) {
        this.localModule.selectionAction = "Clear All";
        return;
      }
      if (enabledCount === flattenedFields.length) {
        this.localModule.selectionAction = "Get All";
      }
    },
    buildFieldValue(moduleName, fieldKey) {
      return `${moduleName}::${fieldKey}`;
    },
    // "Nutrient Management::fertilizerInputs" used to be a flat Text field;
    // it is now the Fertilizer Inputs Group parent, which has no valueType
    // and is therefore never offered as a metric data field. Without this
    // remap, a rule authored against the old key would render blank in the
    // metric picker (getMetricFieldOptions excludes it) and then get
    // silently reassigned to an arbitrary field the next time its type is
    // touched (handleMetricTypeChange). See DIMITRA_LEGACY_DATAFIELD_MAP.
    remapLegacyDataField(dataField) {
      if (!dataField) return dataField;
      return DIMITRA_LEGACY_DATAFIELD_MAP[dataField] || dataField;
    },
    remapLegacyFormulaPartFieldKey(moduleName, fieldKey) {
      if (!moduleName || !fieldKey) return fieldKey;
      const remapped = DIMITRA_LEGACY_DATAFIELD_MAP[this.buildFieldValue(moduleName, fieldKey)];
      if (!remapped) return fieldKey;
      return this.parseFieldValue(remapped).fieldKey || fieldKey;
    },
    parseFieldValue(value) {
      if (!value) {
        return { moduleName: null, fieldKey: null };
      }
      if (String(value).includes("::")) {
        const [rawModuleName, rawFieldKey] = String(value).split("::");
        const moduleName = rawModuleName || null;
        const fieldKey = rawFieldKey || null;
        if (!moduleName || !fieldKey) {
          return { moduleName, fieldKey };
        }

        const normalizedKey = this.toKey(fieldKey);
        const fields = this.getModuleFields(moduleName);
        const matchedField = fields.find(
          (field) =>
            field.key === fieldKey ||
            this.toKey(field.key) === normalizedKey ||
            this.toKey(field.label) === normalizedKey
        );
        if (matchedField) {
          return { moduleName, fieldKey: matchedField.key };
        }
        return { moduleName, fieldKey };
      }

      const normalizedKey = this.toKey(value);
      if (!normalizedKey) {
        return { moduleName: null, fieldKey: null };
      }

      for (const moduleName of this.dimitraSelectedModules) {
        const fields = this.getModuleFields(moduleName);
        const matchedField = fields.find(
          (field) => field.key === normalizedKey || this.toKey(field.key) === normalizedKey || this.toKey(field.label) === normalizedKey
        );
        if (matchedField) {
          return { moduleName, fieldKey: matchedField.key };
        }
      }

      return { moduleName: null, fieldKey: normalizedKey };
    },
    getFieldMeta(moduleName, fieldKey) {
      if (!moduleName || !fieldKey) {
        return null;
      }
      return this.findCatalogFieldByKey(moduleName, fieldKey);
    },
    getFieldMetaFromValue(value) {
      const { moduleName, fieldKey } = this.parseFieldValue(value);
      if (moduleName && fieldKey) {
        return this.getFieldMeta(moduleName, fieldKey);
      }
      if (!fieldKey) {
        return null;
      }

      const modulesToSearch = this.dimitraSelectedModules.length
        ? this.dimitraSelectedModules
        : Object.keys(DIMITRA_CONFIG_TEMPLATE.dimitraModuleCatalog || {});

      for (const name of modulesToSearch) {
        const meta = this.getFieldMeta(name, fieldKey);
        if (meta) {
          return { ...meta, moduleName: name };
        }
      }
      return null;
    },
    // FIXED: Resolves label strictly from catalog via getCatalogLabel
    getFieldLabelFromValue(value) {
      const { moduleName, fieldKey } = this.parseFieldValue(value);
      if (!fieldKey) return "";
      const resolvedModule = moduleName || (() => {
        for (const name of this.dimitraSelectedModules) {
          const meta = this.getFieldMeta(name, fieldKey);
          if (meta) return name;
        }
        return null;
      })();
      if (!resolvedModule) return fieldKey;
      const label = this.getCatalogLabel(resolvedModule, fieldKey);
      return this.dimitraSelectedModules.length > 1
        ? `${this.getModuleDisplayLabel(resolvedModule)} - ${label}`
        : label;
    },
    getModuleFields(moduleName, { numericOnly = false } = {}) {
      const fields = this.getCatalogFieldsFlat(moduleName);
      if (!numericOnly) {
        return fields;
      }
      return fields.filter((field) => field.valueType === "numeric");
    },
    createMetricCondition(rule, operator) {
      return {
        key: this.getProgressMetricRuleKey(rule),
        operator,
        value: null,
        score: null,
      };
    },
    createFormulaDefinition(defaultModule = null) {
      return {
        parts: [
          { module: defaultModule, fieldKey: null, unitId: null, unitTypeId: null, unitAbbvr: null },
          { module: defaultModule, fieldKey: null, unitId: null, unitTypeId: null, unitAbbvr: null },
        ],
        operators: [this.formulaOperators[0]?.value || "divide"],
      };
    },
    getProgressMetricRuleKey(rule) {
      if (!rule) {
        return "";
      }
      if (rule.type === "Formula") {
        return this.toKey(rule.title) || "formula";
      }
      const { moduleName, fieldKey } = this.parseFieldValue(rule.dataField);
      const label = (moduleName && fieldKey) ? this.getCatalogLabel(moduleName, fieldKey) : (fieldKey || rule.dataField || "");
      return this.toKey(label) || "metric";
    },
    syncProgressMetricRule(rule) {
      if (!rule) {
        return;
      }
      rule.key = this.getProgressMetricRuleKey(rule);

      // A dataField change can point the rule at a field with a different
      // (or no) unit type — a previously chosen unit no longer applies.
      if (!this.isMeasurementRule(rule)) {
        rule.unitId = null;
        rule.unitTypeId = null;
        rule.unitAbbvr = null;
      }

      const fallbackOperator = this.progressMetricOperators[rule.type]?.[0] || "=";
      rule.conditions = (rule.conditions || []).map((condition) => ({
        key: rule.key,
        operator: condition.operator || fallbackOperator,
        value: condition.value ?? null,
        score: condition.score ?? null,
      }));

      if (rule.type === "Formula") {
        if (!rule.formula || !Array.isArray(rule.formula.parts)) {
          this.$set(rule, "formula", this.createFormulaDefinition(this.dimitraSelectedModules[0] || null));
        }
      } else if (Object.prototype.hasOwnProperty.call(rule, "formula")) {
        rule.formula = null;
      } else {
        this.$set(rule, "formula", null);
      }
    },
    createProgressMetricRule(type = "State") {
      const operator = this.progressMetricOperators[type]?.[0] || "=";
      const rule = {
        key: "",
        type,
        dataField: null,
        unitId: null,
        unitTypeId: null,
        unitAbbvr: null,
        title: "",
        conditions: [],
        formula: null,
      };

      const options = this.getMetricFieldOptions(rule);
      if (options.length) {
        rule.dataField = options[0].value;
      }

      if (type === "Formula") {
        rule.formula = this.createFormulaDefinition(this.dimitraSelectedModules[0] || null);
      }

      rule.conditions = [this.createMetricCondition(rule, operator)];
      this.syncProgressMetricRule(rule);
      return rule;
    },
    addProgressMetricRule() {
      if (!this.localModule.progressMetrics.rules) {
        this.localModule.progressMetrics.rules = [];
      }
      this.localModule.progressMetrics.rules.push(this.createProgressMetricRule());
    },
    removeProgressMetricRule(index) {
      this.localModule.progressMetrics.rules.splice(index, 1);
      const selectedKey = this.localModule.progressMetrics.yearOnYear?.metricKey;
      if (
        selectedKey &&
        !this.localModule.progressMetrics.rules.find(
          (rule) => this.getProgressMetricRuleKey(rule) === selectedKey
        )
      ) {
        this.localModule.progressMetrics.yearOnYear.metricKey = null;
      }
    },
    handleMetricTypeChange(rule) {
      const operator = this.progressMetricOperators[rule.type]?.[0] || "=";
      rule.conditions = (rule.conditions || []).map((condition) => ({
        ...condition,
        operator,
      }));

      if (!rule.conditions.length) {
        rule.conditions = [this.createMetricCondition(rule, operator)];
      }
      if (rule.type !== "Formula") {
        rule.title = rule.title || "";
      }

      const options = this.getMetricFieldOptions(rule);
      if (rule.dataField && !options.find((option) => option.value === rule.dataField)) {
        rule.dataField = options[0]?.value || null;
      }

      this.syncProgressMetricRule(rule);
    },
    // ✅ FIXED: Uses getCatalogLabel for formula type too, never field.label
    getProgressMetricLabel(rule) {
      if (!rule) return "Metric";
      if (rule.type === "Formula") return rule.title || "Formula";
      return this.getFieldLabelFromValue(rule.dataField) || "Metric";
    },
    // ✅ FIXED: getMetricFieldOptions uses getCatalogLabel for display text
    getMetricFieldOptions(rule) {
      const type = rule?.type || "State";
      const modules = this.dimitraSelectedModules;
      const includeModule = modules.length > 1;
      const options = [];

      modules.forEach((moduleName) => {
        const enabledFields = this.flattenDimitraFieldStates(this.localModule.fieldsByModule?.[moduleName] || [])
          .filter((field) => field.isEnabled);
        const fieldMap = new Map(
          this.getCatalogFieldsFlat(moduleName).map((field) => [field.key, field])
        );

        enabledFields.forEach((field) => {
          const meta = fieldMap.get(field.key);
          const valueType = meta?.valueType;
          if (meta?.excludeFromScoring) {
            return;
          }
          if (type === "State" && valueType !== "text") {
            return;
          }
          if (type === "Condition" && !["numeric", "date"].includes(valueType)) {
            return;
          }
          // ✅ Always use getCatalogLabel for display text in dropdown
          const catalogLabel = this.getCatalogLabel(moduleName, field.key);
          options.push({
            text: includeModule ? `${this.getModuleDisplayLabel(moduleName)} - ${catalogLabel}` : catalogLabel,
            value: this.buildFieldValue(moduleName, field.key),
          });
        });
      });

      return options;
    },
    getConditionLabel(rule) {
      if (rule.type === "Formula") {
        return rule.title || "Formula";
      }
      return this.getFieldLabelFromValue(rule.dataField) || "Select Data Field";
    },
    getConditionOperators(rule) {
      return this.progressMetricOperators[rule.type] || ["="];
    },
    getConditionFieldMeta(rule) {
      if (rule.type === "Formula") {
        return { valueType: "numeric" };
      }
      return this.getFieldMetaFromValue(rule.dataField);
    },
    getConditionValueInputType(rule) {
      const meta = this.getConditionFieldMeta(rule);
      if (meta?.options?.length) {
        return "select";
      }
      if (meta?.valueType === "date") {
        return "date";
      }
      if (meta?.valueType === "numeric") {
        return "number";
      }
      return "text";
    },
    getConditionValueOptions(rule) {
      const meta = this.getConditionFieldMeta(rule);
      return meta?.options || [];
    },
    getConditionValueSuffix(rule) {
      // Formula rules have no single suffix (each part can target a
      // different field/unit), and the admin's chosen unit — not the
      // catalog's static display string — is the one actually being scored
      // against, so it takes priority whenever one has been picked.
      if (rule?.type === "Formula") return "";
      if (rule?.unitAbbvr) return rule.unitAbbvr;
      const meta = this.getConditionFieldMeta(rule);
      return meta?.unit || "";
    },
    // ─── Per-rule unit picker (Measurement Unit fields only) ────────────────
    // Only catalog entries that declare a `unitType` (currently Nutrient
    // Management's fertilizerAppliedArea/applicationRate/ingredientQuantity)
    // participate — everything else keeps comparing raw numbers as before.
    getUnitTypeForField(moduleName, fieldKey) {
      return this.getFieldMeta(moduleName, fieldKey)?.unitType || null;
    },
    isMeasurementField(moduleName, fieldKey) {
      return !!this.getUnitTypeForField(moduleName, fieldKey);
    },
    isMeasurementRule(rule) {
      if (!rule || rule.type === "Formula") return false;
      const { moduleName, fieldKey } = this.parseFieldValue(rule.dataField);
      return this.isMeasurementField(moduleName, fieldKey);
    },
    isMeasurementFormulaPart(part) {
      return this.isMeasurementField(part?.module, part?.fieldKey);
    },
    // Fetches a unit type's UnitsList options once and caches them by name.
    // Called from @focus on the unit dropdowns (matching this file's
    // existing crop/crop-variety autocomplete pattern) plus an eager warm-up
    // in normalizeProgressMetrics for units already saved on a rule.
    async ensureUnitsLoaded(unitTypeName) {
      if (!unitTypeName) return;
      const cached = this.unitTypeCache[unitTypeName];
      if (cached && (cached.loading || cached.loaded)) return;
      this.$set(this.unitTypeCache, unitTypeName, { loading: true, loaded: false, units: [] });
      try {
        const response = await EsgService.getUnitTypes({ name: unitTypeName });
        const unitTypeRow = Array.isArray(response?.data) ? response.data[0] : null;
        const units = Array.isArray(unitTypeRow?.units) ? unitTypeRow.units : [];
        this.$set(this.unitTypeCache, unitTypeName, { loading: false, loaded: true, units });
      } catch (err) {
        this.$set(this.unitTypeCache, unitTypeName, { loading: false, loaded: true, units: [] });
      }
    },
    getUnitOptionsForField(moduleName, fieldKey) {
      const unitTypeName = this.getUnitTypeForField(moduleName, fieldKey);
      if (!unitTypeName) return [];
      const units = this.unitTypeCache[unitTypeName]?.units || [];
      return units.map((unit) => ({ text: `${unit.name} (${unit.abbvr})`, value: unit.id }));
    },
    getUnitOptionsForRule(rule) {
      const { moduleName, fieldKey } = this.parseFieldValue(rule.dataField);
      return this.getUnitOptionsForField(moduleName, fieldKey);
    },
    getUnitOptionsForFormulaPart(part) {
      return this.getUnitOptionsForField(part?.module, part?.fieldKey);
    },
    isUnitOptionsLoading(moduleName, fieldKey) {
      const unitTypeName = this.getUnitTypeForField(moduleName, fieldKey);
      return !!(unitTypeName && this.unitTypeCache[unitTypeName]?.loading);
    },
    isUnitOptionsLoadingForRule(rule) {
      const { moduleName, fieldKey } = this.parseFieldValue(rule.dataField);
      return this.isUnitOptionsLoading(moduleName, fieldKey);
    },
    isUnitOptionsLoadingForFormulaPart(part) {
      return this.isUnitOptionsLoading(part?.module, part?.fieldKey);
    },
    onRuleUnitDropdownFocus(rule) {
      const { moduleName, fieldKey } = this.parseFieldValue(rule.dataField);
      this.ensureUnitsLoaded(this.getUnitTypeForField(moduleName, fieldKey));
    },
    onFormulaUnitDropdownFocus(part) {
      this.ensureUnitsLoaded(this.getUnitTypeForField(part?.module, part?.fieldKey));
    },
    // Denormalizes unitTypeId/abbvr onto the rule/part alongside unitId, so
    // the suffix and the option label render correctly on reload without
    // depending on the unit cache being warm yet.
    resolveUnitDenormalization(unitTypeName, unitId) {
      const units = unitTypeName ? this.unitTypeCache[unitTypeName]?.units || [] : [];
      const unit = units.find((item) => item.id === unitId) || null;
      return {
        unitTypeId: unit?.unitType ?? null,
        unitAbbvr: unit?.abbvr ?? null,
      };
    },
    handleRuleUnitChange(rule) {
      const { moduleName, fieldKey } = this.parseFieldValue(rule.dataField);
      const { unitTypeId, unitAbbvr } = this.resolveUnitDenormalization(
        this.getUnitTypeForField(moduleName, fieldKey), rule.unitId
      );
      rule.unitTypeId = unitTypeId;
      rule.unitAbbvr = unitAbbvr;
    },
    handleFormulaPartUnitChange(part) {
      const { unitTypeId, unitAbbvr } = this.resolveUnitDenormalization(
        this.getUnitTypeForField(part.module, part.fieldKey), part.unitId
      );
      part.unitTypeId = unitTypeId;
      part.unitAbbvr = unitAbbvr;
    },
    // A field-key change invalidates whatever unit was chosen for the
    // previous field (different field can mean a different unit type), so
    // unitId is cleared rather than just re-resolved.
    handleFormulaPartFieldChange(part) {
      part.unitId = null;
      this.handleFormulaPartUnitChange(part);
    },
    addMetricCondition(rule) {
      const operator = this.progressMetricOperators[rule.type]?.[0] || "=";
      rule.conditions.push(this.createMetricCondition(rule, operator));
    },
    removeMetricCondition(rule, index) {
      rule.conditions.splice(index, 1);
    },
    // FIXED: Uses getCatalogLabel for formula field option labels
    getFormulaFieldOptions(moduleName) {
      if (!moduleName) {
        return [];
      }
      const enabledKeys = new Set(
        this.flattenDimitraFieldStates(this.localModule.fieldsByModule?.[moduleName] || [])
          .filter((field) => field.isEnabled)
          .map((field) => field.key)
      );
      return this.getCatalogFieldsFlat(moduleName)
        .filter((field) => field.valueType === "numeric" && !field.excludeFromScoring && enabledKeys.has(field.key))
        .map((field) => ({
          text: this.getCatalogLabel(moduleName, field.key),
          value: field.key,
        }));
    },
    handleFormulaModuleChange(rule, partIndex) {
      const part = rule.formula.parts[partIndex];
      if (!part.module) {
        part.fieldKey = null;
        this.handleFormulaPartUnitChange(part);
        return;
      }
      const available = this.getFormulaFieldOptions(part.module).map((field) => field.value);
      if (part.fieldKey && !available.includes(part.fieldKey)) {
        part.fieldKey = null;
      }
      // A different module invalidates whatever unit was chosen for the
      // previous field. handleFormulaPartUnitChange clears unitTypeId/abbvr
      // when the (module, fieldKey) pair is no longer a measurement field;
      // unitId itself is cleared too since its dropdown options changed.
      part.unitId = null;
      this.handleFormulaPartUnitChange(part);
    },
    addFormulaPart(rule) {
      const defaultModule = this.dimitraSelectedModules[0] || null;
      rule.formula.parts.push({ module: defaultModule, fieldKey: null, unitId: null, unitTypeId: null, unitAbbvr: null });
      rule.formula.operators.push(this.formulaOperators[0]?.value || "divide");
    },
    removeFormulaPart(rule, partIndex) {
      rule.formula.parts.splice(partIndex, 1);
      const opIndex = Math.min(partIndex, rule.formula.operators.length - 1);
      if (opIndex >= 0) {
        rule.formula.operators.splice(opIndex, 1);
      }
    },
    getFormulaOperatorSymbol(value) {
      const operator = this.formulaOperators.find((item) => item.value === value);
      return operator?.symbol || value;
    },
    // FIXED: Uses getCatalogLabel for formula expression display
    buildFormulaExpression(rule) {
      if (!rule?.formula?.parts?.length) {
        return "";
      }

      const parts = rule.formula.parts.map((part) => {
        if (!part.module || !part.fieldKey) {
          return "";
        }
        const label = this.getCatalogLabel(part.module, part.fieldKey);
        if (!label) return "";
        return this.dimitraSelectedModules.length > 1
          ? `${this.getModuleDisplayLabel(part.module)}.${label}`
          : label;
      });

      if (parts.some((part) => !part)) {
        return "";
      }

      return parts.reduce((expression, part, index) => {
        if (index === 0) {
          return part;
        }
        const op = rule.formula.operators[index - 1] || "";
        return `${expression} ${this.getFormulaOperatorSymbol(op)} ${part}`;
      }, "");
    },
    sanitizeProgressMetrics() {
      const modules = new Set(this.dimitraSelectedModules);
      const metrics = this.localModule.progressMetrics;
      if (!metrics || !metrics.rules) {
        return;
      }

      metrics.rules.forEach((rule) => {
        if (rule.type !== "Formula") {
          const { moduleName, fieldKey } = this.parseFieldValue(rule.dataField);
          if (moduleName && !modules.has(moduleName)) {
            rule.dataField = null;
            rule.unitId = null;
            rule.unitTypeId = null;
            rule.unitAbbvr = null;
            return;
          }
          if (moduleName && fieldKey) {
            const meta = this.getFieldMeta(moduleName, fieldKey);
            if (!meta) {
              rule.dataField = null;
              rule.unitId = null;
              rule.unitTypeId = null;
              rule.unitAbbvr = null;
            }
          }
          return;
        }

        if (rule.formula?.parts?.length) {
          rule.formula.parts.forEach((part) => {
            if (part.module && !modules.has(part.module)) {
              part.module = null;
              part.fieldKey = null;
              part.unitId = null;
              part.unitTypeId = null;
              part.unitAbbvr = null;
              return;
            }
            if (part.module && part.fieldKey) {
              const meta = this.getFieldMeta(part.module, part.fieldKey);
              if (!meta || meta.valueType !== "numeric") {
                part.fieldKey = null;
                part.unitId = null;
                part.unitTypeId = null;
                part.unitAbbvr = null;
              }
            }
          });
        }
      });

      if (
        metrics.yearOnYear?.metricKey &&
        !metrics.rules.find((rule) => this.getProgressMetricRuleKey(rule) === metrics.yearOnYear.metricKey)
      ) {
        metrics.yearOnYear.metricKey = null;
      }
    },
    addYoYCondition() {
      if (!this.localModule.progressMetrics.yearOnYear.conditions) {
        this.localModule.progressMetrics.yearOnYear.conditions = [];
      }
      this.localModule.progressMetrics.yearOnYear.conditions.push({
        key: this.toKey(this.localModule.progressMetrics.yearOnYear.metricKey),
        direction: this.yoYComparisonOptions[0],
        percent: null,
        score: null,
      });
    },
    removeYoYCondition(index) {
      this.localModule.progressMetrics.yearOnYear.conditions.splice(index, 1);
    },
    handleYoYMetricChange(metricKey) {
      const key = this.toKey(metricKey);
      this.localModule.progressMetrics.yearOnYear.metricKey = key;
      this.localModule.progressMetrics.yearOnYear.conditions =
        (this.localModule.progressMetrics.yearOnYear.conditions || []).map((condition) => ({
          ...condition,
          key,
        }));
    },
    getYoYMetricLabel() {
      if (!this.localModule.progressMetrics.yearOnYear.metricKey) {
        return "Select Metric";
      }
      const rule = this.localModule.progressMetrics.rules.find(
        (metric) => this.getProgressMetricRuleKey(metric) === this.localModule.progressMetrics.yearOnYear.metricKey
      );
      return this.getProgressMetricLabel(rule);
    },
    onCropDropdownFocus(key) {
      this.activeCropKey = key;
      const state = this.getCropState(key);
      const activeSearch = this.normalizeCropSearch(this.cropSearchMap[key], key) || "";
      this.setCropState(key, { search: activeSearch });

      if (this.shouldRefetchAutocompleteOptions(state, activeSearch) && !state.isFetching) {
        this.fetchCrops({ key, page: 1, search: activeSearch || null, append: false });
      }
      setTimeout(() => {
        this.attachCropMenuScrollListener();
      }, 300);
    },

    attachCropMenuScrollListener() {
      this.detachCropMenuScrollListener();
      const menuContent = document.querySelector('.v-menu__content.menuable__content__active');

      if (!menuContent) return;

      this._cropMenuEl = menuContent;
      this._cropScrollHandler = _.throttle(this.onCropMenuScroll.bind(this), 500);
      this._cropMenuEl.addEventListener('scroll', this._cropScrollHandler, { passive: true });
    },

    detachCropMenuScrollListener() {
      if (this._cropMenuEl && this._cropScrollHandler) {
        this._cropMenuEl.removeEventListener('scroll', this._cropScrollHandler);
        this._cropMenuEl = null;
        this._cropScrollHandler = null;
      }
    },

    onCropMenuScroll(event) {
      const key = this.activeCropKey;
      if (!key) return;

      const state = this.getCropState(key);
      if (state.isFetching || !this.hasMoreCrops(key)) return;

      const el = event.target;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

      const threshold = 500;

      if (distanceFromBottom <= threshold) {
        const nextPage = state.page + 1;
        this.setCropState(key, { page: nextPage });
        this.fetchCrops({
          key,
          page: nextPage,
          search: state.search || null,
          append: true,
        });
      }
    },
    async fetchCrops({ key, page = 1, search = null, append = false } = {}) {
      if (!key) return;

      const state = this.getCropState(key);
      if (append && state.isFetching) return;
      const requestId = state.requestId + 1;

      this.setCropState(key, {
        isFetching: true,
        loading: true,
        page,
        search: search || "",
        requestId,
      });

      try {
        const params = {
          page: page,
          limit: this.cropLimit, // 100
          groupName: 'crop-Type',
        };
        if (search) params.name = search;

        const response = await EsgService.getOptions(params);
        const latestState = this.getCropState(key);
        if (latestState.requestId !== requestId) return;

        if (response && response.success && response.data) {
          const info = response.data.info || [];
          const currentState = this.getCropState(key);

          const newCrops = info.map((item) => ({
            text: item.name,
            value: item.name,
          }));

          if (append) {
            if (newCrops.length > 0) {
              this.setCropState(key, {
                options: _.uniqBy([...currentState.options, ...newCrops], 'value'),
                lastFetchedCount: info.length,
              });
            } else {
              this.setCropState(key, { lastFetchedCount: 0 });
            }
          } else {
            this.setCropState(key, {
              options: newCrops,
              lastFetchedCount: info.length,
            });
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setTimeout(() => {
          const latestState = this.getCropState(key);
          if (latestState.requestId !== requestId) return;
          this.setCropState(key, {
            loading: false,
            isFetching: false,
          });
        }, 800);
      }
    },

    onCropVarietyDropdownFocus(key) {
      this.activeCropVarietyKey = key;
      const state = this.getCropVarietyState(key);
      const activeSearch = this.normalizeCropVarietySearch(this.cropVarietySearchMap[key], key) || "";
      this.setCropVarietyState(key, { search: activeSearch });

      if (this.shouldRefetchAutocompleteOptions(state, activeSearch) && !state.isFetching) {
        this.fetchCropVarieties({ key, page: 1, search: activeSearch || null, append: false });
      }
      setTimeout(() => {
        this.attachCropVarietyMenuScrollListener();
      }, 300);
    },

    attachCropVarietyMenuScrollListener() {
      this.detachCropVarietyMenuScrollListener();
      const menuContent = document.querySelector('.v-menu__content.menuable__content__active');
      if (!menuContent) return;
      this._cropVarietyMenuEl = menuContent;
      this._cropVarietyScrollHandler = _.throttle(this.onCropVarietyMenuScroll.bind(this), 500);
      this._cropVarietyMenuEl.addEventListener('scroll', this._cropVarietyScrollHandler, { passive: true });
    },

    detachCropVarietyMenuScrollListener() {
      if (this._cropVarietyMenuEl && this._cropVarietyScrollHandler) {
        this._cropVarietyMenuEl.removeEventListener('scroll', this._cropVarietyScrollHandler);
        this._cropVarietyMenuEl = null;
        this._cropVarietyScrollHandler = null;
      }
    },

    onCropVarietyMenuScroll(event) {
      const key = this.activeCropVarietyKey;
      if (!key) return;

      const state = this.getCropVarietyState(key);
      if (state.isFetching || !this.hasMoreCropVarieties(key)) return;

      const el = event.target;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom <= 500) {
        const nextPage = state.page + 1;
        this.setCropVarietyState(key, { page: nextPage });
        this.fetchCropVarieties({ key, page: nextPage, search: state.search || null, append: true });
      }
    },

    async fetchCropVarieties({ key, page = 1, search = null, append = false } = {}) {
      if (!key) return;

      const state = this.getCropVarietyState(key);
      if (append && state.isFetching) return;
      const requestId = state.requestId + 1;

      this.setCropVarietyState(key, {
        isFetching: true,
        loading: true,
        page,
        search: search || "",
        requestId,
      });
      try {
        const params = {
          limit: this.cropVarietyLimit,
          offset: (page - 1) * this.cropVarietyLimit,
        };
        if (search) params.name = search;

        const response = await EsgService.getCropVarieties(params);
        const latestState = this.getCropVarietyState(key);
        if (latestState.requestId !== requestId) return;

        if (response && response.data.data) {
          const items = Array.isArray(response.data.data) ? response.data.data : [];
          const currentState = this.getCropVarietyState(key);

          const newVarieties = items.map(item => ({
            text: item.name,
            value: item.name,
          }));

          if (append) {
            if (newVarieties.length > 0) {
              this.setCropVarietyState(key, {
                options: _.uniqBy([...currentState.options, ...newVarieties], 'value'),
                lastFetchedCount: items.length,
              });
            } else {
              this.setCropVarietyState(key, { lastFetchedCount: 0 });
            }
          } else {
            this.setCropVarietyState(key, {
              options: newVarieties,
              lastFetchedCount: items.length,
            });
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setTimeout(() => {
          const latestState = this.getCropVarietyState(key);
          if (latestState.requestId !== requestId) return;
          this.setCropVarietyState(key, {
            loading: false,
            isFetching: false,
          });
        }, 800);
      }
    },
  },
  beforeDestroy() {
    this.detachCropMenuScrollListener();
    this.detachCropVarietyMenuScrollListener();
  }
};
</script>

<style lang="scss" scoped>
h3,
h4 {
  color: black;
}

.h3 {
  font-size: 1.2em;
  font-weight: bold;
}

.content-col {
  display: flex;
  align-items: center;
}

.bottomless {
  background-color: transparent;
}

.dimitra-selection {
  max-width: 160px;
  background-color: #FFFFFF;
}

.group-wrapper {
  background-color: #F3F3F3;
  overflow: hidden;
}

.group-child-field {
  background-color: white;
}
.group-child-field .row{
  margin:0;
}

::v-deep .bottomless .v-input__slot {
  margin-bottom: 0 !important;
}

::v-deep .bottomless .v-messages {
  display: none !important;
}

::v-deep .options .v-card__title {
  font-size: 1em;
  font-weight: 900;
  color: white;
  background-color: rgb(66, 66, 66);
  border-radius: 8px !important;
}

.pointer {
  cursor: pointer;
}

.progress-metric-card {
  border-radius: 8px;
}

.condition-row {
  background-color: #f4f4f4;
  border-radius: 8px;
}

.readonly-field ::v-deep .v-input__slot {
  background-color: #ececec !important;
}

.formula-part {
  position: relative;
  border: 1px dashed #000000;
  border-radius: 8px;
  padding: 12px;
}

.formula-remove {
  position: absolute;
  top: 6px;
  right: 6px;
}

.formula-operator {
  max-width: 160px;
}

.add-link {
  text-transform: none;
}

.score-input {
  max-width: 110px;
}
.group-header{
  background-color: #DDDDDD;
}
.group-body{
  background-color: #F3F3F3;
}

</style>